import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Shield, CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import BrandTitle from '../components/BrandTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PROVIDERS = [
  {
    id: 'okta',
    name: 'Okta',
    type: 'OIDC',
    logo: (
      <div className="w-9 h-9 rounded-xl bg-blue-500 bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold text-sm shadow-md">
        O
      </div>
    ),
  },
  {
    id: 'microsoft',
    name: 'Microsoft Entra ID',
    subtitle: 'Azure Active Directory',
    type: 'OIDC',
    logo: (
      <div className="w-9 h-9 rounded-xl bg-sky-500 bg-gradient-to-br from-sky-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm shadow-md">
        M
      </div>
    ),
  },
  {
    id: 'google',
    name: 'Google Workspace',
    type: 'OIDC',
    logo: (
      <div className="w-9 h-9 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-sm font-bold shadow-md">
        <span className="bg-blue-500 bg-gradient-to-br from-blue-500 via-red-500 to-yellow-500 bg-clip-text text-transparent">G</span>
      </div>
    ),
  },
];

const SSOLogin = () => {
  const navigate = useNavigate();
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [workEmail, setWorkEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('provider'); // 'provider' | 'workemail' | 'notconfigured' | 'redirecting'
  const [notConfiguredMsg, setNotConfiguredMsg] = useState('');
  const [providerStatus, setProviderStatus] = useState({}); // {google:true|false,...}

  // Fetch which providers are configured on mount
  useEffect(() => {
    axios.get(`${API_URL}/api/sso/oidc/status`)
      .then(r => setProviderStatus(r.data || {}))
      .catch(() => setProviderStatus({}));
  }, []);

  const handleProviderSelect = (provider) => {
    setSelectedProvider(provider);
    setStep('workemail');
  };

  const handleContinue = async (e) => {
    e.preventDefault();
    if (!workEmail.includes('@')) {
      toast.error('Please enter a valid work email');
      return;
    }
    setLoading(true);

    try {
      const resp = await axios.get(`${API_URL}/api/sso/oidc/initiate`, {
        params: { provider: selectedProvider.id },
      });
      if (!resp.data.configured) {
        setNotConfiguredMsg(resp.data.message || `${selectedProvider.name} is not configured yet.`);
        setStep('notconfigured');
        setLoading(false);
        return;
      }
      // Redirect to IdP
      setStep('redirecting');
      window.location.href = resp.data.redirect_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to start ${selectedProvider.name} sign-in.`);
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen relative overflow-hidden flex items-center justify-center p-6"
      data-testid="sso-login-page"
      style={{
        background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(99, 102, 241, 0.08) 0%, transparent 50%), radial-gradient(ellipse 60% 50% at 90% 100%, rgba(6, 182, 212, 0.06) 0%, transparent 50%), #ffffff'
      }}
    >
      <div className="absolute top-[-200px] left-[-200px] w-[600px] h-[600px] rounded-full bg-gradient-to-br from-indigo-300/15 to-transparent blur-3xl pointer-events-none" />
      <div className="absolute bottom-[-200px] right-[-200px] w-[600px] h-[600px] rounded-full bg-gradient-to-tr from-cyan-300/15 to-transparent blur-3xl pointer-events-none" />

      <div className="relative w-full max-w-md">
        <button
          onClick={() => navigate('/login')}
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-6 transition-colors relative"
          data-testid="sso-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to regular login
        </button>

        <div className="dark relative">
          {/* Glow ring */}
          <div className="absolute -inset-1 bg-gradient-to-br from-indigo-500/30 via-cyan-500/15 to-emerald-500/20 rounded-3xl blur-2xl pointer-events-none" />

          {/* Dark floating card */}
          <div className="relative rounded-3xl bg-slate-950 border border-slate-800 shadow-2xl shadow-cyan-500/10 overflow-hidden text-white">
            <div className="h-1 bg-blue-500 bg-gradient-to-r from-blue-500 via-cyan-500 to-emerald-500" />

            <div className="p-8">
              {/* Heading inside dark card */}
              <div className="text-center mb-7">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-indigo-500 bg-gradient-to-br from-indigo-500 to-cyan-600 shadow-lg shadow-indigo-500/30 mb-4">
                  <Shield className="w-7 h-7 text-white" />
                </div>
                <h1 className="text-xl font-bold text-white"><BrandTitle>Enterprise Single Sign-On</BrandTitle></h1>
                <p className="text-sm text-slate-400 mt-1">Sign in with your company identity provider</p>
              </div>

              {step === 'provider' && (
                <div className="space-y-3" data-testid="sso-provider-list">
                  <p className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest mb-2">
                    Choose your provider
                  </p>
                  {PROVIDERS.map((p) => {
                    const isConfigured = providerStatus[p.id];
                    return (
                      <button
                        key={p.id}
                        onClick={() => handleProviderSelect(p)}
                        className="w-full flex items-center gap-3 p-4 rounded-xl border border-slate-700 bg-slate-900 hover:border-indigo-500 hover:bg-slate-800 transition-all group text-left"
                        data-testid={`sso-provider-${p.id}`}
                      >
                        {p.logo}
                        <div className="flex-1">
                          <p className="font-semibold text-sm text-white group-hover:text-indigo-300">{p.name}</p>
                          {p.subtitle && <p className="text-xs text-slate-400">{p.subtitle}</p>}
                        </div>
                        {isConfigured ? (
                          <span className="text-[10px] font-bold text-emerald-300 px-2 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/30 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" />Live
                          </span>
                        ) : (
                          <span className="text-[10px] font-bold text-slate-400 group-hover:text-indigo-300 px-2 py-1 rounded-md bg-slate-800 border border-slate-700 group-hover:border-indigo-500/40">
                            {p.type}
                          </span>
                        )}
                      </button>
                    );
                  })}

                  <div className="pt-4 border-t border-slate-800 mt-4">
                    <p className="text-xs text-slate-400 text-center">
                      Don't see your provider?{' '}
                      <Link to="/contact" className="text-indigo-400 hover:text-indigo-300 hover:underline font-medium">
                        Request support
                      </Link>
                    </p>
                  </div>
                </div>
              )}

              {step === 'workemail' && (
                <form onSubmit={handleContinue} className="space-y-5" data-testid="sso-email-form">
                  <div className="flex items-center gap-3 p-3 bg-emerald-500/10 rounded-xl border border-indigo-500/30">
                    {selectedProvider.logo}
                    <div className="flex-1">
                      <p className="font-semibold text-sm text-white">{selectedProvider.name}</p>
                      <p className="text-xs text-slate-400">{selectedProvider.type} authentication</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setStep('provider')}
                      className="text-xs text-indigo-300 hover:text-indigo-200 hover:underline font-medium"
                      data-testid="sso-change-provider"
                    >
                      Change
                    </button>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="work-email">Work Email</Label>
                    <Input
                      id="work-email"
                      type="email"
                      placeholder="you@yourcompany.com"
                      value={workEmail}
                      onChange={(e) => setWorkEmail(e.target.value)}
                      required
                      className="h-11 auth-input"
                      data-testid="sso-work-email-input"
                      autoFocus
                    />
                    <p className="text-xs text-slate-400">
                      We'll redirect you to {selectedProvider.name} to complete sign-in.
                    </p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-11 bg-indigo-600 bg-gradient-to-r from-indigo-600 to-cyan-600 hover:from-indigo-700 hover:to-cyan-700 text-white"
                    disabled={loading}
                    data-testid="sso-continue-btn"
                  >
                    {loading ? 'Connecting…' : `Continue to ${selectedProvider.name}`}
                  </Button>
                </form>
              )}

              {step === 'redirecting' && (
                <div className="py-10 text-center" data-testid="sso-redirecting">
                  <Loader2 className="w-10 h-10 text-indigo-400 mx-auto animate-spin mb-4" />
                  <p className="font-semibold text-white">Redirecting to {selectedProvider?.name}</p>
                  <p className="text-sm text-slate-400 mt-1">Please hold on…</p>
                </div>
              )}

              {step === 'notconfigured' && (
                <div className="py-6 text-center space-y-4" data-testid="sso-not-configured">
                  <div className="w-14 h-14 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mx-auto">
                    <AlertTriangle className="w-7 h-7 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">SSO not configured yet</h3>
                    <p className="text-sm text-slate-400 mt-2 max-w-sm mx-auto">{notConfiguredMsg}</p>
                  </div>
                  <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl text-left space-y-2">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                      <p className="text-xs text-slate-300">Admin registers an OAuth app at the provider (5 min)</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                      <p className="text-xs text-slate-300">Credentials added to CARTIEAI → sign-in goes live instantly</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full border-slate-700 text-slate-200 hover:bg-slate-800"
                    onClick={() => { setStep('provider'); setWorkEmail(''); }}
                    data-testid="sso-try-again-btn"
                  >
                    Try a different provider
                  </Button>
                  <Link to="/login" className="block text-sm text-indigo-400 hover:text-indigo-300 hover:underline font-medium">
                    Use password instead →
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 mt-6 relative">
          Protected by OpenID Connect · SOC 2 compliant
        </p>
      </div>
    </div>
  );
};

export default SSOLogin;
